import React, { useId, useState } from "react"
import { Box, Divider, IconButton, Menu, MenuItem, Stack, Toolbar, Typography } from "@mui/material"
import { Link } from "react-router-dom"
import { User } from "firebase/auth"
import MoreVertIcon from "@mui/icons-material/MoreVert"

import CMCAvatar from "@/shared/components/Avatar"
import { ThemeMode } from "@/shared/context/ThemeContext"
import { usePlan } from "@/shared/hooks/usePlan"
import NavLinks from "@/shared/components/appbar/NavLinks"
import SidebarFooter from "@/shared/components/appbar/SidebarFooter"
import SitemarkIcon from "@/features/landing/template/components/SitemarkIcon"

interface Props {
  currentPath: string
  onNavigate: () => void
  mode: ThemeMode
  toggleTheme: () => void
  user: User | null
  onPasswordReset: () => void
  onLogout: () => void
}

const DrawerContent: React.FC<Props> = ({
  currentPath,
  onNavigate,
  mode,
  toggleTheme,
  user,
  onPasswordReset,
  onLogout
}) => {
  const { creditsRemaining } = usePlan()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const menuOpen = Boolean(anchorEl)
  const menuButtonId = useId()
  const menuId = useId()

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handlePasswordReset = () => {
    handleMenuClose()
    onPasswordReset()
  }

  const handleLogout = () => {
    handleMenuClose()
    onLogout()
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Toolbar>
        <Link to="/" style={{ textDecoration: "none" }}>
          <Stack direction="row" spacing={1}>
            <SitemarkIcon />
          </Stack>
        </Link>
      </Toolbar>
      <Divider />
      <NavLinks currentPath={currentPath} onNavigate={onNavigate} />
      <Box sx={{ flex: 1 }} />
      <SidebarFooter
        mode={mode}
        toggleTheme={toggleTheme}
        creditsRemaining={creditsRemaining}
        onNavigate={onNavigate}
      />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.5 }}>
        <Link
          to="/profile"
          onClick={onNavigate}
          style={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
            color: "inherit",
            minWidth: 0,
            flex: 1
          }}>
          <CMCAvatar alt={user?.displayName || ""} size={32} url={user?.photoURL || null} uid={null} />
          <Stack sx={{ ml: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {user?.displayName}
            </Typography>
            {user?.email && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {user.email}
              </Typography>
            )}
          </Stack>
        </Link>
        <IconButton
          id={menuButtonId}
          aria-label="account actions"
          aria-controls={menuOpen ? menuId : undefined}
          aria-haspopup="true"
          aria-expanded={menuOpen ? "true" : undefined}
          onClick={handleMenuOpen}
          size="small">
          <MoreVertIcon />
        </IconButton>
        <Menu
          id={menuId}
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "right" }}
          MenuListProps={{ "aria-labelledby": menuButtonId }}>
          <MenuItem onClick={handlePasswordReset} disabled={!user?.email}>
            Change Password
          </MenuItem>
          <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
            Sign Out
          </MenuItem>
        </Menu>
      </Stack>
    </Box>
  )
}

export default DrawerContent
